import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { team_getAll, team_getMemberInTask } from '../../../services/team';
import { Team, TabKey } from '../types';
// import { uid } from 'uid';

const RESTRICTED_ROLE_ID = 'ecbf4355-952b-4cc2-8136-8353ca55b0d2';

interface UseTeamDataProps {
  taskId: string;
  tabKey: TabKey;
  currentUserId?: string;
  currentUserRoleId?: string;
  searchQuery?: string;
}

export const useTeamData = ({ taskId, tabKey, currentUserId, currentUserRoleId, searchQuery }: UseTeamDataProps) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);

  const getAllTeams = useCallback(async () => {
    setLoading(true);
    try {
      const data = {
        task_id: taskId,
        query: searchQuery || '',
        include_member: true,
      };
      const res = await team_getAll(data);

      if (res.status === 'success' && Array.isArray(res.data)) {
        const teamsData: Team[] = res.data.map((team: any) => {
          // ตรวจสอบทุก field ที่อาจเป็น status
          const teamStatus = team.status || team.team_status || team.Status || team.active || 1;
          return {
            id: team.team_id,
            name: team.team_name,
            members: Array.isArray(team.members)
              ? team.members.map((member: any) => ({
                  id: member.user_id,
                  user_id: member.user_id,
                  name: member.name,
                  role: member.team_role,
                  email: member.email,
                  role_id: member.role_id,
                  task_role: member.task_role ?? null,
                }))
              : [],
            createdAt: team.created_timestamp || new Date().toISOString(),
            status: Number(teamStatus), // แปลงเป็น number
          };
        });

        // Filter: ผู้ใช้ที่มี RESTRICTED_ROLE_ID จะไม่เห็นทีมที่มีสมาชิกซึ่งเป็น task admin (task_role = 2)
        const filtered = currentUserRoleId === RESTRICTED_ROLE_ID
          ? teamsData.filter((team) =>
              !team.members.some((m) => m.task_role === 2)
            )
          : teamsData;

        // Sort: ทีมที่ตัวเองอยู่ขึ้นก่อน
        const sorted = currentUserId
          ? [...filtered].sort((a, b) => {
              const aIsMember = a.members.some((m) => (m.user_id || m.id) === currentUserId) ? -1 : 1;
              const bIsMember = b.members.some((m) => (m.user_id || m.id) === currentUserId) ? -1 : 1;
              return aIsMember - bIsMember;
            })
          : filtered;

        setTeams(sorted);
      } else {
        message.error('ไม่สามารถดึงข้อมูลทีมได้');
        setTeams([]);
      }
    } catch (error) {
      console.error('Error fetching all teams:', error);
      message.error('เกิดข้อผิดพลาดในการดึงข้อมูล');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, currentUserRoleId, searchQuery, taskId]);

  const getAllMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = {
        task_id: taskId,
        page: 1,
        size: 9999,
      };
      const res = await team_getMemberInTask(data);

      if (res.status === 'success' && Array.isArray(res.data)) {
        let members: Team[] = res.data.map((member: any) => ({
          id: member.user_id,
          name: member.name || 'N/A',
          team_name: member.team_name || null,
          team_id: member.team_id || null,
          members: [
            {
              id: member.user_id,
              user_id: member.user_id,
              name: member.name,
              role: member.team_role ?? null,
              email: member.email,
              task_role: member.task_role ?? null,
            },
          ],
          createdAt: new Date().toISOString(),
          status: member.status ?? 1,
        }));

        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          members = members.filter((m) => {
            const email = m.members[0]?.email?.toLowerCase() || '';
            const teamName = ((m as any).team_name || '').toLowerCase();
            return m.name.toLowerCase().includes(q) || email.includes(q) || teamName.includes(q);
          });
        }

        setTeams(members);
      } else {
        message.error('ไม่สามารถดึงข้อมูลสมาชิกได้');
        setTeams([]);
      }
    } catch (error) {
      console.error('Error fetching all members:', error);
      message.error('เกิดข้อผิดพลาดในการดึงข้อมูล');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [taskId, searchQuery]);

  const refreshData = useCallback(() => {
    if (tabKey === 'all') {
      getAllTeams();
    } else if (tabKey === 'user') {
      getAllMembers();
    }
  }, [getAllMembers, getAllTeams, tabKey]);

  const updateTeamStatus = (teamId: string, newStatus: number) => {
    setTeams((prevTeams) =>
      prevTeams.map((team) =>
        team.id === teamId ? { ...team, status: newStatus } : team
      )
    );
  };

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    teams,
    loading,
    refreshData,
    updateTeamStatus,
    getAllTeams,
    getAllMembers,
  };
};
